import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * PDF del Piano Editoriale da mostrare al cliente PRIMA della produzione.
 *
 * Solo dati condivisibili: data, formato, piattaforma, titolo e hook.
 * Niente stati interni, incaricati, note, ads o spending.
 * Generazione client-side con jsPDF (stesso stile di fatturaPDF).
 */

const C = {
  bg: [7, 7, 26], cyan: [0, 180, 220], text: [30, 35, 50],
  muted: [110, 120, 140], border: [220, 225, 235], bgLight: [248, 249, 252],
  white: [255, 255, 255],
};

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
// Larghezza utile della colonna Contenuto: A4 - margini - colonne fisse - padding
const CONTENT_W = 210 - 16 * 2 - (24 + 26 + 26) - 6;
// Interlinea (mm) di titolo e hook nella colonna Contenuto, e spazio tra i due
const LH_TITOLO = 3.9, LH_HOOK = 3.5, GAP = 1.2;
const GIORNI = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

// I font standard di jsPDF coprono solo Latin-1: normalizza la tipografia "smart"
// e toglie emoji/simboli che uscirebbero come caratteri spuri.
function pulisci(s) {
  return String(s || '')
    .replace(/[‘’‚]/g, "'")
    .replace(/[“”„]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(/[^\t\n\r\x20-\x7E\xA0-\xFF€]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

const meseTitolo = (k) => { const [y, m] = k.split('-'); return `${MESI[Number(m) - 1]} ${y}`; };

function dataCella(r) {
  const d = (r.data_pubblicazione || '').slice(0, 10);
  if (!d) return 'Da definire';
  const [y, m, g] = d.split('-').map(Number);
  const dt = new Date(y, m - 1, g);
  const ora = r.ora_pubblicazione ? `\n${String(r.ora_pubblicazione).slice(0, 5)}` : '';
  return `${GIORNI[dt.getDay()]} ${String(g).padStart(2, '0')}/${String(m).padStart(2, '0')}${ora}`;
}

/**
 * @param {object}  opts
 * @param {string}  opts.cliente  nome/azienda del cliente
 * @param {Array}   opts.righe    righe PED (già filtrate per periodo)
 * @param {string}  opts.mese     'YYYY-MM' se è un mese solo, '' se "tutti i prossimi"
 */
export function generatePedPDF({ cliente, righe, mese }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, M = 16;
  const periodo = mese ? meseTitolo(mese) : 'Prossimi contenuti';

  // ── Header ──
  doc.setFillColor(...C.bg); doc.rect(0, 0, W, 30, 'F');
  doc.setFillColor(...C.cyan); doc.rect(0, 0, 4, 30, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...C.cyan);
  doc.text('PIANO EDITORIALE', M, 13);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(200, 210, 230);
  doc.text(pulisci(cliente), M, 20);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(...C.white);
  doc.text(periodo, W - M, 13, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(200, 210, 230);
  doc.text(`${righe.length} contenut${righe.length === 1 ? 'o' : 'i'}`, W - M, 20, { align: 'right' });

  // ── Righe, raggruppate per mese (le senza data in fondo) ──
  const ord = [...righe].sort((a, b) => {
    const da = `${a.data_pubblicazione || '9999'}${a.ora_pubblicazione || ''}`;
    const db = `${b.data_pubblicazione || '9999'}${b.ora_pubblicazione || ''}`;
    return da.localeCompare(db);
  });
  const gruppi = [];
  ord.forEach(r => {
    const k = (r.data_pubblicazione || '').slice(0, 7) || 'senza-data';
    let g = gruppi[gruppi.length - 1];
    if (!g || g.k !== k) { g = { k, righe: [] }; gruppi.push(g); }
    g.righe.push(r);
  });

  const body = [];
  gruppi.forEach(g => {
    // Intestazione di mese solo se il PDF copre più mesi
    if (!mese || gruppi.length > 1) {
      body.push([{
        content: g.k === 'senza-data' ? 'Data da definire' : meseTitolo(g.k), colSpan: 4,
        styles: { fillColor: C.bgLight, textColor: C.cyan, fontStyle: 'bold', fontSize: 9 },
      }]);
    }
    g.righe.forEach(r => {
      const titolo = pulisci(r.titolo_post) || 'Senza titolo';
      const hook = pulisci(r.hook);
      body.push([dataCella(r), pulisci(r.formato), pulisci(r.piattaforma), { content: titolo, hook }]);
    });
  });

  autoTable(doc, {
    startY: 38,
    head: [['Data', 'Formato', 'Piattaforma', 'Contenuto']],
    body,
    theme: 'grid',
    headStyles: { fillColor: C.bg, textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
    bodyStyles: { fontSize: 9, textColor: C.text, cellPadding: 3, valign: 'top', lineColor: C.border },
    columnStyles: {
      0: { cellWidth: 24 },
      1: { cellWidth: 26 },
      2: { cellWidth: 26 },
      3: { cellWidth: 'auto', fontStyle: 'bold' },
    },
    margin: { left: M, right: M, bottom: 18 },
    rowPageBreak: 'avoid',
    // L'hook va sotto al titolo, in corsivo e più piccolo: autoTable non mescola stili
    // nella stessa cella, quindi riserviamo l'altezza giusta e lo disegnamo a mano.
    didParseCell: (d) => {
      const raw = d.cell.raw;
      if (d.section !== 'body' || d.column.index !== 3 || !raw || !raw.hook) return;
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
      const tl = doc.splitTextToSize(raw.content, CONTENT_W);
      doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5);
      const hl = doc.splitTextToSize(`"${raw.hook}"`, CONTENT_W);
      d.cell.styles.minCellHeight = 6 + tl.length * LH_TITOLO + GAP + hl.length * LH_HOOK;
    },
    willDrawCell: (d) => {
      const raw = d.cell.raw;
      if (d.section === 'body' && d.column.index === 3 && raw && raw.hook) d.cell.text = [];
    },
    didDrawCell: (d) => {
      const raw = d.cell.raw;
      if (d.section !== 'body' || d.column.index !== 3 || !raw || !raw.hook) return;
      const x = d.cell.x + 3;
      let y = d.cell.y + 3 + 3.2;
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...C.text);
      const tl = doc.splitTextToSize(raw.content, CONTENT_W);
      doc.text(tl, x, y);
      y += tl.length * LH_TITOLO + GAP;
      doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5); doc.setTextColor(...C.muted);
      doc.text(doc.splitTextToSize(`"${raw.hook}"`, CONTENT_W), x, y, { lineHeightFactor: 1.15 });
    },
    didDrawPage: () => {
      const n = doc.internal.getNumberOfPages();
      doc.setDrawColor(...C.border); doc.setLineWidth(0.4); doc.line(M, 284, W - M, 284);
      doc.setFont('helvetica', 'italic'); doc.setFontSize(7.5); doc.setTextColor(...C.muted);
      doc.text(`Bozza da approvare - generato il ${new Date().toLocaleDateString('it-IT')}`, M, 289);
      doc.text(`Pagina ${n}`, W - M, 289, { align: 'right' });
    },
  });

  const slug = pulisci(cliente).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'cliente';
  doc.save(`piano-editoriale_${slug}_${mese || 'prossimi'}.pdf`);
}
